import React from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import AlertBox from '@/components/layout/AlertBox';
import type { RadiusUserGroupDTO } from '@webip/shared/src/dto/radius_user';

/**
 * Lado LAN da coerência VLAN ↔ grupo RADIUS. O grupo guarda só o número da tag
 * (vale pra N appliances), então quem edita a VLAN de uma LAN precisa saber
 * quais grupos direcionam usuários pra ela nesta appliance — mudar ou remover
 * a tag deixa esses usuários sem rede aqui, sem erro em lugar nenhum.
 * Só aviso: a tela de RADIUS mostra o mesmo cruzamento do outro lado.
 */
export default function AvisoVlanRadius({
    applianceId,
    redeId,
    vlan,
}: {
    applianceId: number;
    redeId: number | undefined;
    vlan: number | undefined;
}) {
    // VLAN salva da rede em edição, pra detectar troca/remoção. Reinicia quando
    // o formulário passa a editar outra rede.
    const [original, setOriginal] = React.useState({ redeId, vlan: vlan ?? 0 });
    if (original.redeId !== redeId) setOriginal({ redeId, vlan: vlan ?? 0 });

    const query = useQuery(
        ['radius-groups'],
        async () => {
            const res = await axios.get<{ ok: boolean; result: RadiusUserGroupDTO[] }>(
                '/api/radius/groups',
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        { staleTime: 60_000, refetchOnWindowFocus: false },
    );

    const gruposNaVlan = (tag: number) =>
        (query.data ?? []).filter(
            (g) =>
                g.ativo &&
                g.vlan_id === tag &&
                (g.appliance_scope === 'all' ||
                    (g.appliance_scope === 'specific' && (g.appliance_ids ?? []).includes(applianceId))),
        );

    const atual = vlan ?? 0;
    const orig = redeId != null ? original.vlan : 0;

    if (orig > 0 && orig !== atual) {
        const afetados = gruposNaVlan(orig);
        if (afetados.length) {
            return (
                <AlertBox variant="warning">
                    {afetados.length === 1 ? 'O grupo RADIUS ' : 'Os grupos RADIUS '}
                    <strong>{afetados.map((g) => g.nome).join(', ')}</strong>
                    {afetados.length === 1 ? ' direciona' : ' direcionam'} usuários para a VLAN {orig}. Ao{' '}
                    {atual > 0 ? `trocar para ${atual}` : 'remover a VLAN'}, eles ficarão sem rede nesta appliance.
                </AlertBox>
            );
        }
    }

    if (atual > 0) {
        const usando = gruposNaVlan(atual);
        if (usando.length) {
            return (
                <AlertBox variant="info">
                    Grupos RADIUS que colocam usuários nesta VLAN:{' '}
                    <strong>{usando.map((g) => g.nome).join(', ')}</strong>.
                </AlertBox>
            );
        }
    }

    return null;
}
